import { Alert, Snackbar } from '@mui/material';
import { IRuleExecution } from '@scale/shared/types/ruleEngine/rule';
import { useState } from 'react';
import ExecutionRow from 'react-scale/corp/components/RuleEngine/ExecutionRow';
import apiFetch from 'react-scale/lib/api-fetch';
import { IStepExecution } from '@scale/shared/types/ruleEngine/step';
import { useMutation } from 'react-query';
import { useUser } from 'react-scale/components/ui/UserContext';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

function RuleExecutionRow(props: {
  ruleExecution: IRuleExecution;
  label?: string;
  displayUtcTime?: boolean;
  invalidateRuleExecutions: () => void;
}) {
  const user = useUser();
  const { ruleExecution, label, displayUtcTime, invalidateRuleExecutions } = props;
  const [localRuleExecution, setLocalRuleExecution] = useState<IRuleExecution>(ruleExecution);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { mutateAsync: stopRuleExecution, isLoading: isStoppingRuleExecution } = useMutation({
    mutationFn: async () =>
      apiFetch(null, `corp-api/rules/executions/${ruleExecution._id}/stop`, {
        method: 'PUT',
      }),
    onSuccess: () => invalidateRuleExecutions(),
  });

  const auditRuleExecution = async (approved: boolean): Promise<void> => {
    try {
      const response = await apiFetch<IRuleExecution>(
        null,
        `corp-api/rules/executions/${ruleExecution._id}/approve`,
        {
          method: 'POST',
          body: { approved },
        },
      );
      setLocalRuleExecution(response);
    } catch (error) {
      if (error.response && error.response.message) {
        setErrorMessage(error.response.message);
      }
    }
    return;
  };

  const canStopRuleExecution = useUnleashFeatureFlag(
    'stop-rule-execution',
    { userId: user.id },
    false,
  );

  return (
    <>
      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(undefined)}
      >
        <Alert onClose={() => setErrorMessage(undefined)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <ExecutionRow
        label={label}
        execution={localRuleExecution}
        displayUtcTime={displayUtcTime}
        stopRuleExecution={canStopRuleExecution ? stopRuleExecution : undefined}
        isStoppingRuleExecution={isStoppingRuleExecution}
      >
        {localRuleExecution.steps &&
          localRuleExecution.steps.map((stepExecution, indx) => (
            <ExecutionRow
              label={stepExecution.type}
              execution={stepExecution as IStepExecution & { _id: string }}
              key={indx}
              auditExecution={auditRuleExecution}
              displayUtcTime={displayUtcTime}
            />
          ))}
      </ExecutionRow>
    </>
  );
}

export default RuleExecutionRow;
